<script setup lang="ts">
  import type { OrderDelivery } from '~/types/ecommerce/generated.codegen';

  const WHATSAPP_NUMBER = '5511912386374';

  type DeliveryUiStatus =
    | 'analysis'
    | 'pending'
    | 'waiting_product'
    | 'shipped'
    | 'delivered';

  const CIF_STATUS = {
    analysis: { label: 'Em análise', type: 'informative' },
    pending: { label: 'Aguardando separação', type: 'warning' },
    waiting_product: { label: 'Aguardando produto', type: 'warning' },
    shipped: { label: 'Rastreio disponível', type: 'positive' },
    delivered: { label: 'Entregue', type: 'positive' },
  } as const;

  const FOB_STATUS = {
    ...CIF_STATUS,
    shipped: { label: 'Retirada disponível', type: 'positive' },
    delivered: { label: 'Retirado', type: 'positive' },
  } as const;

  const PENDING_PAYMENT_STATUS = {
    label: 'Aguardando pagamento',
    type: 'warning',
  } as const;

  const FOB_SCHEDULED_STATUS = {
    label: 'Retirada disponível',
    type: 'positive',
  } as const;

  const CIF_TRACKING_DISCLAIMER =
    'Quando a entrega for enviada, você poderá acompanhar o rastreio.';
  const FOB_AFTER_SEPARATION_DISCLAIMER =
    'Esta retirada ficará disponível após a separação.';

  const props = defineProps<{
    delivery: OrderDelivery;
    isFob?: boolean;
    pendingPayment?: boolean;
    partialInvoicing?: boolean;
    availableAtDate?: string | null;
    schedulePickupText?: string | null;
  }>();

  const emit = defineEmits<{
    'download-shipping': [];
  }>();

  const status = computed(() => props.delivery.status as DeliveryUiStatus);
  const isAwaitingPayment = computed(
    () =>
      status.value === 'analysis' ||
      (props.delivery.total <= 1 && Boolean(props.pendingPayment)),
  );
  const canOpenTracking = computed(
    () =>
      ['shipped', 'delivered'].includes(status.value) &&
      Boolean(props.delivery.tracking_link?.trim()),
  );

  const statusConfig = computed(() => {
    if (props.delivery.total <= 1 && props.pendingPayment)
      return PENDING_PAYMENT_STATUS;
    if (isDeliveryScheduled.value && status.value === 'pending') {
      return FOB_SCHEDULED_STATUS;
    }
    const map = props.isFob ? FOB_STATUS : CIF_STATUS;
    return map[status.value] ?? map.pending;
  });

  const deliveryLabel = computed(() => {
    const word = props.isFob ? 'Retirada' : 'Entrega';
    return props.delivery.total > 1 ? `${word} ${props.delivery.index}` : word;
  });

  const showShippingNf = computed(
    () =>
      props.delivery.total > 1 &&
      Boolean(props.delivery.invoice_number?.trim()),
  );

  const isPickupScheduled = computed(
    () => props.schedulePickupText === 'Retirada agendada',
  );

  const isDeliveryScheduled = computed(
    () =>
      props.isFob &&
      (['shipped', 'delivered'].includes(status.value) ||
        Boolean(props.delivery.invoice_number)),
  );

  const showFobPreSchedule = computed(
    () =>
      props.isFob &&
      !isDeliveryScheduled.value &&
      !isPickupScheduled.value &&
      (status.value === 'analysis' ||
        status.value === 'pending' ||
        status.value === 'waiting_product'),
  );

  const showFobPendingWithTag = computed(
    () =>
      props.isFob &&
      showFobPreSchedule.value &&
      (props.partialInvoicing || props.delivery.total > 1),
  );

  const showFobPreScheduleSingle = computed(
    () =>
      props.isFob &&
      showFobPreSchedule.value &&
      !props.partialInvoicing &&
      props.delivery.total <= 1,
  );

  const showCifDefaultTracking = computed(
    () => !props.isFob && props.delivery.total <= 1 && !canOpenTracking.value,
  );

  const showStatusTag = computed(
    () => !(props.delivery.total <= 1 && props.pendingPayment),
  );

  const isFobScheduleDisabled = computed(
    () => isAwaitingPayment.value || status.value === 'waiting_product',
  );

  function openTracking() {
    if (!props.delivery.tracking_link) return;
    navigateTo(props.delivery.tracking_link, {
      external: true,
      open: { target: '_blank' },
    });
  }

  function schedulePickup() {
    navigateTo(`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}`, {
      external: true,
      open: { target: '_blank' },
    });
  }
</script>

<template>
  <div class="delivery-card">
    <template v-if="showFobPendingWithTag">
      <div class="delivery-card__row">
        <div class="delivery-card__row-left">
          <span class="delivery-card__label">{{ deliveryLabel }}</span>
          <SolTag
            :id="`status-tag-${delivery.id}`"
            variant="outline"
            size="small"
            :type="statusConfig.type">
            {{ statusConfig.label }}
          </SolTag>
        </div>
      </div>
      <p
        v-if="status === 'waiting_product' && availableAtDate"
        class="delivery-card__disclaimer">
        <icon-girassol:info class="delivery-card__disclaimer-icon" />
        Previsão de chegada no CD: {{ availableAtDate }}
      </p>
      <p
        v-else-if="status === 'pending'"
        class="delivery-card__disclaimer">
        <icon-girassol:info class="delivery-card__disclaimer-icon" />
        {{ FOB_AFTER_SEPARATION_DISCLAIMER }}
      </p>
    </template>

    <template v-else-if="showFobPreScheduleSingle">
        <div class="delivery-card__pre-separation-row">
          <p class="delivery-card__pre-separation-disclaimer">
            <icon-girassol:info class="delivery-card__disclaimer-icon" />
            <span v-if="isAwaitingPayment"
              >O botão de agendamento ficará disponível após o pagamento do
              pedido.</span
            >
            <span v-else-if="status === 'waiting_product'"
              >O botão de agendamento ficará disponível após o produto chegar no
              CD.</span
            >
            <span v-else
              >Este pedido pode ser quebrado em mais de uma retirada no momento
              do agendamento, a depender do tamanho do caminhão.</span
            >
          </p>
          <SolButton
            :id="`schedule-pickup-${delivery.id}`"
            :variant="isFobScheduleDisabled ? 'secondary' : 'primary'"
            size="medium"
            :disabled="isFobScheduleDisabled"
            :class="[
              'delivery-card__schedule-btn',
              {
                'delivery-card__track-btn--pending': isFobScheduleDisabled,
              },
            ]"
            @click="schedulePickup">
            <template #icon:left>
              <icon-girassol:whatsapp />
            </template>
            Agendar retirada
          </SolButton>
        </div>
    </template>

    <template v-else-if="showCifDefaultTracking">
      <div class="delivery-card__pre-separation-row">
        <div class="delivery-card__pre-separation-disclaimer">
          <icon-girassol:info class="delivery-card__disclaimer-icon" />
          <p>
            {{ CIF_TRACKING_DISCLAIMER }}
          </p>
        </div>
        <SolButton
          :id="`track-delivery-${delivery.id}`"
          variant="secondary"
          size="medium"
          disabled
          class="delivery-card__track-btn delivery-card__track-btn--pending">
          Rastrear entrega
        </SolButton>
      </div>
    </template>

    <template v-else>
      <div class="delivery-card__row">
        <div class="delivery-card__row-left">
          <span class="delivery-card__label">{{ deliveryLabel }}</span>
          <SolTag
            v-if="showStatusTag"
            :id="`status-tag-${delivery.id}`"
            variant="outline"
            size="small"
            :type="statusConfig.type">
            {{ statusConfig.label }}
          </SolTag>
        </div>
        <SolButton
          v-if="!isFob"
          :id="`track-delivery-${delivery.id}`"
          :variant="canOpenTracking ? 'primary' : 'secondary'"
          size="medium"
          :disabled="!canOpenTracking"
          :class="[
            'delivery-card__track-btn',
            { 'delivery-card__track-btn--pending': !canOpenTracking },
          ]"
          @click="openTracking">
          Rastrear entrega
        </SolButton>
      </div>

      <p
        v-if="!isFob && !canOpenTracking && status !== 'waiting_product'"
        class="delivery-card__disclaimer">
        <icon-girassol:info class="delivery-card__disclaimer-icon" />
        {{ CIF_TRACKING_DISCLAIMER }}
      </p>

      <p
        v-if="
          !isFob &&
          !isAwaitingPayment &&
          status === 'waiting_product' &&
          availableAtDate
        "
        class="delivery-card__disclaimer">
        <icon-girassol:info class="delivery-card__disclaimer-icon" />
        Previsão de chegada no CD: {{ availableAtDate }}
      </p>
    </template>

    <div v-if="showShippingNf" class="delivery-card__docs-section">
      <span class="delivery-card__docs-label">NOTA FISCAL DE REMESSA</span>
      <button
        type="button"
        class="delivery-card__nfe-link"
        @click="emit('download-shipping')">
        <icon-girassol:description class="delivery-card__nfe-link-icon" />
        Baixar NF-e desta {{ isFob ? 'retirada' : 'entrega' }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .delivery-card {
    @apply bg-neutral-high-pure;
    @apply flex flex-col gap-micro px-2xs py-4xs;

    &__row {
      @apply flex flex-col gap-micro sm:site:flex-row sm:site:items-center sm:site:justify-between sm:site:gap-nano;
    }

    &__row-left {
      @apply flex flex-row flex-wrap items-center gap-nano;
    }

    &__label {
      @apply fonts-body-large-bold text-neutral-low-dark;
    }

    &__pre-separation-row {
      @apply flex flex-col-reverse gap-micro sm:site:flex-row sm:site:items-center sm:site:justify-between sm:site:gap-2xs;
    }

    &__pre-separation-disclaimer {
      @apply flex min-w-0 flex-1 items-center gap-quark;
      @apply fonts-body-medium-regular text-neutral-low-light;
    }

    &__schedule-btn {
      @apply w-full shrink-0 sm:site:w-auto;
    }

    &__docs-section {
      @apply flex flex-col gap-quark;
    }

    &__docs-label {
      @apply fonts-body-small-bold uppercase text-neutral-low-light;
    }

    &__nfe-link {
      @apply inline-flex w-fit self-start items-center gap-quark p-0 m-0;
      @apply fonts-body-medium-regular text-neutral-low-dark;
      @apply cursor-pointer border-none bg-transparent text-left underline;

      &:hover {
        @apply text-neutral-low-medium;
      }

      &:focus-visible {
        @apply rounded-sm ring-2 ring-feedback-informative-medium ring-offset-2;
      }
    }

    &__nfe-link-icon {
      @apply icon-size-small shrink-0;
    }

    &__track-btn {
      @apply w-full min-w-fit shrink-0 sm:site:w-auto;
    }

    &__track-btn--pending {
      &.sol-button-core,
      &.sol-button-core.--disabled,
      &.sol-button-core:disabled {
        @apply border-neutral-high-medium bg-neutral-high-medium text-neutral-low-dark text-opacity-medium;
      }
    }

    &__disclaimer {
      @apply flex items-center gap-quark;
      @apply fonts-body-medium-regular text-neutral-low-light;
    }

    &__disclaimer-icon {
      @apply icon-size-small shrink-0 text-neutral-low-light;
    }
  }
</style>
